import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Appointment, AvailabilitySimple } from '@dreryk/egabinet-appointment-frontend-api';

import { commonTranslationKeys, useFormController } from '@dreryk/react-common';
import { Modal, ModalWidth } from '@dreryk/react-components';

import { useHealthcareLocationQuery } from '~legacy.healthcare-provider';

import { useAdjustStartTimeOrEndTime, useRecurrencePeriodSupplier } from '../common/common.hooks';
import { findDefaultAvailabilityTypeId } from '../common/common.utils';
import { useSearchAvailabilityTypes } from '../common/queries/searchAvailabilityTypes';

import {
   useConfirmRemovingAvailabilityMessage,
   useConfirmUpdateAvailabilityMessage,
   useCustomButtonsConfiguration,
   useInitialValues,
   useUpdateSingleOccurrenceSupplier,
} from './availabilityUpdate.hooks';
import { AvailabilityUpdateFormBean } from './availabilityUpdate.types';
import { setBookingChannels, setRecurrencePeriodRadioValue } from './AvailabilityUpdate.utils';
import { AvailabilityUpdateModalTitle } from './AvailabilityUpdateModalTitle';
import { AvailabilityUpdatePanel } from './AvailabilityUpdatePanel';
import { AvailabilityUpdatePanelLoader } from './AvailabilityUpdatePanel.loader';
import { usePerformAppointmentOutOfUpdatingOrRemovingAvailabilityCqrsQuery } from './queries/usePerformAppointmentOutOfUpdatingOrRemovingAvailabilityCqrsQuery';
import { useGetAvailability } from './queries/getAvailability';
import { useRemoveAvailability } from './queries/removeAvailability';
import { useUpdateAvailabilityInFormView } from './queries/updateAvailabilityInForm';

type AvailabilityUpdateModalProps = {
   readonly onClose: VoidFunction;
   readonly selectedEvent: AvailabilitySimple;
   readonly refetch: VoidFunction;
};

enum AvailabilityUpdateModalActionType {
   REMOVE = 'REMOVE',
   UPDATE = 'UPDATE',
}

// eslint-disable-next-line max-lines-per-function,max-statements
const AvailabilityUpdateModal = (props: AvailabilityUpdateModalProps): JSX.Element => {
   const [removalConfirmationModalOpen, setRemovalConfirmationModalOpen] = useState(false);
   const [updateConfirmationModalOpen, setUpdateConfirmationModalOpen] = useState(false);
   const { executeAsync: removeAvailability } = useRemoveAvailability(
      props.refetch,
      props.onClose,
      props.selectedEvent.practitioner.id
   );
   const { executeAsync: updateAvailability } = useUpdateAvailabilityInFormView(
      props.selectedEvent.practitioner.id,
      props.refetch,
      props.onClose
   );
   const { data: availabilityTypes } = useSearchAvailabilityTypes();
   const initialValues = useInitialValues(props.selectedEvent);
   const {
      register,
      getValue,
      getValues,
      setValue,
      handleSubmit,
      watch,
      formState,
      trigger,
      sync,
   } = useFormController<AvailabilityUpdateFormBean>(initialValues);
   const updateSingleOccurrenceSupplier = useUpdateSingleOccurrenceSupplier(getValue);

   const {
      appointment: appointmentOutOfUpdatingOrRemovingAvailability,
      fetchData: performAppointmentOutOfUpdateOrRemovingAvailabilityCqrsQuery,
   } = usePerformAppointmentOutOfUpdatingOrRemovingAvailabilityCqrsQuery(props.selectedEvent.practitioner.id);

   const getFormData = useCallback((): AvailabilityUpdateFormBean => {
      return getValues() as AvailabilityUpdateFormBean;
   }, [getValues]);

   const handleUpdateAvailability = useCallback(async (): Promise<void> => {
      const formData = getFormData();
      // eslint-disable-next-line no-return-await
      return await updateAvailability({
         ...formData,
         id: props.selectedEvent.id,
      });
   }, [getFormData, props.selectedEvent.id, updateAvailability]);
   const handleRemoveAvailability = async (): Promise<void> => {
      const formData = getFormData();
      // eslint-disable-next-line no-return-await
      return await removeAvailability({
         id: props.selectedEvent.id,
         removeSingleOccurrence: formData.updateSingleOccurrence,
         availabilityDate: props.selectedEvent.availabilityDate,
      });
   };
   const onFindAppointmentForAvailabilityUpdateSuccess = useCallback(
      async (data: Appointment | null, actionType: AvailabilityUpdateModalActionType): Promise<void> => {
         switch (actionType) {
            case AvailabilityUpdateModalActionType.REMOVE: {
               setRemovalConfirmationModalOpen(true);
               break;
            }
            case AvailabilityUpdateModalActionType.UPDATE:
            default: {
               if (data) {
                  setUpdateConfirmationModalOpen(true);
               }
               if (!data) {
                  await handleUpdateAvailability();
               }
            }
         }
      },
      [handleUpdateAvailability]
   );
   const { data: availability, loading: availabilityLoading } = useGetAvailability(
      props.selectedEvent.id,
      props.selectedEvent.availabilityDate,
      props.selectedEvent.practitioner.id
   );
   const { data: healthcareLocations, loading: healthcareLocationsLoading } = useHealthcareLocationQuery();

   const defaultAvailabilityTypeId = useMemo(() => findDefaultAvailabilityTypeId(availabilityTypes), [
      availabilityTypes,
   ]);

   const performModalAction = useCallback(
      async (actionType: AvailabilityUpdateModalActionType): Promise<void> => {
         const { availabilityDate } = initialValues;
         const formData = getFormData();
         const appointment = await performAppointmentOutOfUpdateOrRemovingAvailabilityCqrsQuery({
            availabilityId: props.selectedEvent.id,
            updateSingleOccurrence: updateSingleOccurrenceSupplier(),
            newStartTime: actionType === AvailabilityUpdateModalActionType.UPDATE ? formData.startTime : undefined,
            newEndTime: actionType === AvailabilityUpdateModalActionType.UPDATE ? formData.endTime : undefined,
            selectedAvailabilityDate: availabilityDate,
         });
         await onFindAppointmentForAvailabilityUpdateSuccess(appointment, actionType);
      },
      [
         getFormData,
         initialValues,
         onFindAppointmentForAvailabilityUpdateSuccess,
         performAppointmentOutOfUpdateOrRemovingAvailabilityCqrsQuery,
         props.selectedEvent.id,
         updateSingleOccurrenceSupplier,
      ]
   );

   const handleAvailabilityUpdateInit = handleSubmit(async () =>
      performModalAction(AvailabilityUpdateModalActionType.UPDATE)
   );

   const { customButtonsConfiguration } = useCustomButtonsConfiguration(handleAvailabilityUpdateInit, async () =>
      performModalAction(AvailabilityUpdateModalActionType.REMOVE)
   );

   const adjustStartTimeOrEndTime = useAdjustStartTimeOrEndTime(getValue, setValue);

   useEffect(() => {
      const { docplannerEncounterMode, patientPortalEncounterMode } = setBookingChannels(availability?.bookingChannels);
      sync({
         ...getValues(),
         recurrencePeriod: setRecurrencePeriodRadioValue(availability?.recurrencePeriodInWeeks),
         sendToDocplanner: Boolean(docplannerEncounterMode),
         sendToPatientPortal: Boolean(patientPortalEncounterMode),
         patientPortalEncounterMode: patientPortalEncounterMode ?? undefined,
         docplannerEncounterMode: docplannerEncounterMode ?? undefined,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [availability]);

   useEffect(() => {
      if (defaultAvailabilityTypeId && !getValue('availabilityTypeId')) {
         setValue('availabilityTypeId', defaultAvailabilityTypeId);
      }
   }, [defaultAvailabilityTypeId, getValue, setValue]);

   const confirmRemovingAvailabilityMessage = useConfirmRemovingAvailabilityMessage(
      updateSingleOccurrenceSupplier,
      !props.selectedEvent.recurring,
      appointmentOutOfUpdatingOrRemovingAvailability
   );
   const confirmUpdateAvailabilityMessage = useConfirmUpdateAvailabilityMessage(
      updateSingleOccurrenceSupplier,
      !props.selectedEvent.recurring,
      appointmentOutOfUpdatingOrRemovingAvailability
   );

   return (
      <>
         <Modal
            title={<AvailabilityUpdateModalTitle recurring={props.selectedEvent.recurring} />}
            footerItems={customButtonsConfiguration}
            wrapInForm
            onSubmit={noop}
            onCancel={props.onClose}
            onCloseRequest={props.onClose}
            width={ModalWidth.NORMAL}
            testId={'availabilityUpdate'}
         >
            <AvailabilityUpdatePanelLoader
               availabilityLoading={availabilityLoading}
               availability={availability}
               healthcareLocationsLoading={healthcareLocationsLoading}
               healthcareLocations={healthcareLocations}
            >
               <AvailabilityUpdatePanel
                  register={register}
                  watch={watch}
                  formState={formState}
                  trigger={trigger}
                  recurrencePeriodSupplier={useRecurrencePeriodSupplier(getValue('recurrencePeriod'))}
                  availabilityTypes={availabilityTypes ?? []}
                  adjustStartTimeOrEndTime={adjustStartTimeOrEndTime}
                  recurringAvailability={props.selectedEvent.recurring}
                  practitioner={props.selectedEvent.practitioner}
                  defaultLocationIdentifier={availability?.location}
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                  healthcareLocations={healthcareLocations!}
               />
            </AvailabilityUpdatePanelLoader>
         </Modal>
         {removalConfirmationModalOpen ? (
            <Modal
               title={commonTranslationKeys.confirm}
               confirmationButtonLabel={commonTranslationKeys.ok}
               onConfirm={handleRemoveAvailability}
               onCancel={() => setRemovalConfirmationModalOpen(false)}
               cancelButtonLabel={commonTranslationKeys.cancel}
               onCloseRequest={() => setRemovalConfirmationModalOpen(false)}
               testId={'confirmRemove'}
            >
               {confirmRemovingAvailabilityMessage}
            </Modal>
         ) : null}
         {updateConfirmationModalOpen && confirmUpdateAvailabilityMessage ? (
            <Modal
               title={commonTranslationKeys.confirm}
               confirmationButtonLabel={commonTranslationKeys.ok}
               onConfirm={handleUpdateAvailability}
               onCancel={() => setUpdateConfirmationModalOpen(false)}
               cancelButtonLabel={commonTranslationKeys.cancel}
               onCloseRequest={() => setUpdateConfirmationModalOpen(false)}
               testId={'confirmUpdate'}
            >
               {confirmUpdateAvailabilityMessage}
            </Modal>
         ) : null}
      </>
   );
};

export { AvailabilityUpdateModal };
